// Pure-SVG multi-series line chart styled with Chakra theme tokens: scaled
// area+line paths inside an SVG, surrounded by crisp Chakra axis labels.
import { Box, Flex, Text, useColorModeValue, useToken } from "@chakra-ui/react";

/** French month abbreviations used as the default x-axis labels. */
export const MONTH_LABELS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Aoû", "Sep", "Oct", "Nov", "Déc"];

/**
 * Builds polyline and closed area-polygon point strings in a 0..100 viewBox.
 * @param {Array<number>} values - per-step numeric values
 * @param {number} max - scale maximum (must be > 0)
 * @returns {{line: string, area: string}} SVG point strings
 */
function buildPoints(values, max) {
  const n = values.length;
  const pts = values.map((v, i) => {
    const x = n > 1 ? (i / (n - 1)) * 100 : 50;
    const y = 100 - (max ? (v / max) * 100 : 0);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });
  return { line: pts.join(" "), area: `0,100 ${pts.join(" ")} 100,100` };
}

/**
 * Renders a responsive line chart for one or more series, with 0/mid/max y
 * ticks and month x labels drawn as crisp Chakra text around the scaled SVG.
 * @param {Object} props - datasets ([{label, values, color}]), labels, height,
 *   tickColor/gridColor (optional overrides for non theme-following surfaces)
 * @returns {React.ReactElement}
 */
export default function SvgLineChart({ datasets, labels = MONTH_LABELS, height = "300px", tickColor, gridColor }) {
  const [gridLight, gridDark] = useToken("colors", ["secondaryGray.400", "navy.600"]);
  const themeGrid = useColorModeValue(gridLight, gridDark);
  const themeTick = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const grid = gridColor || themeGrid;
  const tick = tickColor || themeTick;
  const allValues = datasets.flatMap((d) => d.values || []);
  const max = Math.max(1, ...allValues);
  const series = datasets.map((d) => ({ color: d.color, ...buildPoints(d.values || [], max) }));
  return (
    <Box>
      <Flex h={height}>
        <Flex direction="column" justify="space-between" w="32px" py="2px" pr="6px" textAlign="right">
          {[max, Math.round(max / 2), 0].map((t, i) => (
            <Text key={i} fontSize="10px" color={tick}>{t}</Text>
          ))}
        </Flex>
        <Box position="relative" flex="1">
          <Box as="svg" w="100%" h="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
            {[0, 50, 100].map((y) => (
              <line key={y} x1="0" y1={y} x2="100" y2={y} stroke={grid} strokeWidth="0.5"
                vectorEffect="non-scaling-stroke" />
            ))}
            {series.map((s, i) => (
              <g key={i}>
                <polygon points={s.area} fill={s.color} fillOpacity="0.12" />
                <polyline points={s.line} fill="none" stroke={s.color} strokeWidth="2"
                  strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
              </g>
            ))}
          </Box>
        </Box>
      </Flex>
      <Flex pl="32px" justify="space-between" mt="6px">
        {labels.map((m, i) => <Text key={i} fontSize="9px" color={tick}>{m}</Text>)}
      </Flex>
    </Box>
  );
}
